import React from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";
import { UI_MODAL, UI_MODAL_OVERLAY, UI_DIVIDER } from "lib/uiClasses";

/**
 * Portal-based modal primitive.
 * Handles focus restore, ESC close, scroll lock, and body click-outside.
 * A small in-memory stack keeps nested modals on monotonically increasing z-index
 * so overlay stacking never has to be hand-tuned.
 */
const modalStack: Symbol[] = [];
const BASE_Z = 1200;

function useZIndex(open: boolean): number {
  const keyRef = React.useRef<Symbol>(Symbol("modal"));
  const [z, setZ] = React.useState(BASE_Z);

  React.useEffect(() => {
    if (!open) return;
    modalStack.push(keyRef.current);
    setZ(BASE_Z + (modalStack.length - 1) * 10);
    const key = keyRef.current;
    return () => {
      const idx = modalStack.indexOf(key);
      if (idx >= 0) modalStack.splice(idx, 1);
    };
  }, [open]);

  return z;
}

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Hide default close button (still closable via ESC / overlay). */
  hideClose?: boolean;
  /** Block overlay click. */
  dismissOnOverlay?: boolean;
};

const SIZE_CLASS: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

export default function Modal({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  hideClose = false,
  dismissOnOverlay = true,
}: ModalProps) {
  const z = useZIndex(open);
  const previousActive = React.useRef<Element | null>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Only depend on `open`. Parent re-renders often pass a new inline `onClose`; re-running
  // this effect would restore focus to the pre-modal element and blur modal inputs.
  React.useEffect(() => {
    if (!open) return;
    previousActive.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (previousActive.current instanceof HTMLElement) {
        previousActive.current.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open) return null;

  const content = (
    <div
      className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4"
      style={{ zIndex: z }}
      onMouseDown={(e) => {
        if (dismissOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      <div className={UI_MODAL_OVERLAY} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative my-auto flex max-h-[90vh] w-full flex-col overflow-hidden ${SIZE_CLASS[size]} ${UI_MODAL}`}
      >
        {(title || !hideClose) && (
          <div className={`flex shrink-0 items-start justify-between gap-4 border-b px-5 py-4 ${UI_DIVIDER}`}>
            <h3 className="text-base font-bold text-gray-800 dark:text-slate-50">{title}</h3>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-100"
                aria-label="Close"
              >
                <MdClose className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className={`flex shrink-0 justify-end gap-2 border-t px-5 py-3 ${UI_DIVIDER}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
